/**
 * Shared utilities for list extensions (OrderedListWithStart, BulletListWithStyle).
 * Plugin logic lives in plugins/getListPatternPlugin.ts.
 */

import type { ResolvedPos, Node as PMNode, NodeType } from '@tiptap/pm/model';
import { NodeSelection, Transaction } from '@tiptap/pm/state';

export type TListHit = { node: PMNode; pos: number } | null;

/** Patch attributes on a node at `pos`. Returns false if node not found or no-op. */
export const setAttrsAtPos = (
  tr: Transaction,
  pos: number,
  type: NodeType,
  patch: Record<string, unknown>
): boolean => {
  const node = tr.doc.nodeAt(pos);
  if (!node || node.type !== type) return false;
  const nextAttrs = { ...node.attrs, ...patch };
  const same = Object.keys(patch).every((k) => node.attrs[k] === patch[k]);
  if (same) return false;
  const isSelected = tr.selection instanceof NodeSelection && tr.selection.from === pos;
  // Not setNodeAttribute: its step has no range, so history would make each label keystroke its own undo.
  tr.setNodeMarkup(pos, node.type, nextAttrs, node.marks);
  // The new markup maps a node selection to a caret.
  if (isSelected) tr.setSelection(NodeSelection.create(tr.doc, pos));
  return true;
};

/**
 * Find the root (outermost) list within the current list tree.
 * Continues through listItem nodes and text blocks. Stops at structural
 * containers (doc, tableCell, section, table, etc.).
 */
export const getRootList = ($from: ResolvedPos, listTypeName: string): TListHit => {
  let root: TListHit = null;
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    const name = node.type.name;
    if (name === listTypeName) {
      root = { node, pos: $from.before(d) };
    } else if (name === 'listItem' || node.isTextblock) {
      continue;
    } else {
      break;
    }
  }
  return root;
};

/** Set `listPattern` on a list node and all its nested list descendants. */
export const patchPatternTree = (
  tr: Transaction,
  pos: number,
  node: PMNode,
  listType: NodeType,
  pattern: string | null
) => {
  setAttrsAtPos(tr, pos, listType, { listPattern: pattern });
  node.descendants((child, childOffset) => {
    if (child.type === listType) {
      setAttrsAtPos(tr, pos + 1 + childOffset, listType, { listPattern: pattern });
    }
    return true;
  });
};
